import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Baby, Search, ChevronDown, ChevronUp, Users, UserPlus, Trash2, Settings, Ban, Unlock, MailCheck, Link2 } from "lucide-react";
import { getAuthErrorMessage, loginInputClass } from "../LoginForm";
import { primaryButtonClass } from "./ProfilTab";
import { Avatar } from "../ProfileMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");
const ROLE_LABELS = {
  vezetobiro: "Vezetőbíró", biro: "Bíró", stilusbiro: "Stílusbíró", dijlovasbiro: "Díjlovas bíró", lovastusabiro: "Lovastusa bíró",
  melegitobiro: "Melegítőpálya bíró", versenyszervezo: "Versenyszervező", palyaepito: "Pályaépítő", versenyiroda: "Versenyiroda",
};
const roleLabel = (r) => ROLE_LABELS[r] || r;
const PERM_COLORS = { "ALAP REV": "bg-emerald-100 text-emerald-800", "REV": "bg-red-100 text-red-700", "GLAV": "bg-purple-100 text-purple-800" };
const BadgeGroup = ({ title, items, color, testId }) => items?.length ? (
  <div data-testid={testId}>
    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">{title}</p>
    <div className="flex flex-wrap gap-1">
      {items.map(([key, label, c]) => <Badge key={key} color={c || color} testId={`${testId}-${key.replace(/\s+/g, "-").toLowerCase()}`}>{label}</Badge>)}
    </div>
  </div>
) : null;
const PermBadges = ({ perms, testId }) => <BadgeGroup title="Rajtengedély" items={(perms || []).map((p) => [p, p, PERM_COLORS[p]])} color="bg-slate-100 text-slate-700" testId={testId} />;
const RoleBadges = ({ roles, testId }) => <BadgeGroup title="Jogosultság" items={(roles || []).map((r) => [r, roleLabel(r)])} color="bg-sky-100 text-sky-800" testId={testId} />;
const genderLabel = (g) => (g === "no" ? "Nő / Lány" : g === "ferfi" ? "Férfi / Fiú" : "Nincs megadva");

const Badge = ({ color, children, testId }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${color}`} data-testid={testId}>{children}</span>
);

export function FelhasznalokTab() {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [modal, setModal] = useState(null); // "user" | "child"
  const [editUser, setEditUser] = useState(null);
  const [verifyUser, setVerifyUser] = useState(null);
  const [linksUser, setLinksUser] = useState(null);
  const [deletingId, setDeletingId] = useState("");

  const removeUser = async (u) => {
    if (!window.confirm(`Biztosan törlöd a(z) ${u.name} fiókot? A művelet nem visszavonható, és törli a kapcsolódó adatokat (nevezések, lovak stb.).`)) return;
    setDeletingId(u.id);
    try {
      await axios.delete(`${API}/competition/users/${u.id}`, { params: { token: token() } });
      toast.success("Fiók törölve");
      load();
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setDeletingId("");
  };

  const load = async () => {
    try {
      const u = await axios.get(`${API}/competition/users`, { params: { token: token() } });
      setUsers(u.data || []);
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
      setUsers([]);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleSuspend = async (u) => {
    const suspend = !u.is_suspended;
    if (suspend && !window.confirm(`Biztosan felfüggeszted a(z) ${u.name} fiókot? A fiók és az adatai megmaradnak, de nem tud bejelentkezni.`)) return;
    setBusy(u.id);
    try {
      const res = await axios.post(`${API}/ugyfelkapu/admin/users/${u.id}/suspend`, { suspended: suspend }, { params: { token: token() } });
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setBusy("");
  };

  const byId = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);
  const parentsOf = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => (u.linked_account_ids || []).forEach((cid) => { (m[cid] = m[cid] || []).push(u); }));
    return m;
  }, [users]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return [...(users || [])]
      .filter((u) => !needle || [u.name, u.identifier, u.email].some((v) => (v || "").toLowerCase().includes(needle)))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "hu"));
  }, [users, q]);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="felhasznalok-tab">
      <section className="rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100" data-testid="users-section">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Users size={20} className="text-slate-700" /> Felhasználók {users && <Badge color="bg-slate-100 text-slate-700" testId="users-count">{users.length} fiók</Badge>}</h2>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setModal("user")} className={primaryButtonClass} data-testid="admin-add-user-btn"><UserPlus size={16} /> Fiók hozzáadása</button>
            <button type="button" onClick={() => setModal("child")} className={primaryButtonClass} data-testid="admin-add-child-btn"><Baby size={16} /> Gyerekfiók hozzáadása</button>
          </div>
        </div>
        <div className="relative mb-4">
          <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés név, azonosító vagy e-mail alapján" className={`${loginInputClass} pl-11`} data-testid="users-search-input" />
        </div>
        {!users ? (
          <p className="py-8 text-center text-slate-600">Betöltés...</p>
        ) : filtered.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-600" data-testid="users-empty">Nincs találat.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="users-list">
            {filtered.map((u) => {
              const open = openId === u.id;
              const linked = (u.linked_account_ids || []).map((id) => byId[id]).filter(Boolean);
              const parents = parentsOf[u.id] || [];
              return (
                <li key={u.id} data-testid={`user-row-${u.id}`}>
                  <button type="button" onClick={() => setOpenId(open ? null : u.id)} className="flex w-full items-center gap-3 py-3 text-left hover:bg-slate-50 rounded-xl px-2 -mx-2" data-testid={`user-toggle-${u.id}`}>
                    <Avatar user={u} className="h-10 w-10 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={u.is_suspended ? "font-semibold text-slate-400 line-through" : "font-semibold text-slate-800"} data-testid={`user-name-${u.id}`}>{u.name}</span>
                        <span className="font-mono text-xs font-semibold tracking-wider text-slate-700" data-testid={`user-identifier-${u.id}`}>{u.identifier || "—"}</span>
                        {u.is_suspended && <Badge color="bg-slate-200 text-slate-700" testId={`user-suspended-badge-${u.id}`}>Felfüggesztve</Badge>}
                        {u.is_admin && <Badge color="bg-slate-800 text-white">Admin</Badge>}
                        {u.is_child_account && <Badge color="bg-sky-100 text-sky-800">Gyerek</Badge>}
                      </div>
                      <p className="truncate text-xs text-slate-700">{u.email || (u.is_child_account ? "Nincs e-mail" : "")}{u.birth_date ? ` · Szül.: ${u.birth_date}` : ""}</p>
                    </div>
                    <div className="hidden sm:flex max-w-[45%] shrink-0 flex-row items-start gap-4">
                      <PermBadges perms={u.start_permissions} testId={`user-perms-${u.id}`} />
                      <RoleBadges roles={u.roles} testId={`user-roles-${u.id}`} />
                    </div>
                    {open ? <ChevronUp size={18} className="shrink-0 text-slate-600" /> : <ChevronDown size={18} className="shrink-0 text-slate-600" />}
                  </button>
                  {open && (
                    <div className="mb-3 rounded-2xl bg-slate-50 p-4 text-sm" data-testid={`user-details-${u.id}`}>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                        <div><dt className="text-xs text-slate-700">Azonosító</dt><dd className="font-mono font-bold text-slate-800">{u.identifier || "—"}</dd></div>
                        <div><dt className="text-xs text-slate-700">Nem</dt><dd className="text-slate-800">{genderLabel(u.gender)}</dd></div>
                        <div><dt className="text-xs text-slate-700">Születési dátum</dt><dd className="text-slate-800">{u.birth_date || "—"}</dd></div>
                        <div><dt className="text-xs text-slate-700">E-mail</dt><dd className="truncate text-slate-800">{u.email || "—"}</dd></div>
                        <div><dt className="text-xs text-slate-700">Telefon</dt><dd className="text-slate-800">{u.phone || "—"}</dd></div>
                        <div><dt className="text-xs text-slate-700">Fiók típus</dt><dd className="text-slate-800">{u.is_admin ? "Adminisztrátor" : u.is_child_account ? "Gyerekfiók" : "Normál"}</dd></div>
                        <div className="col-span-2 sm:col-span-3 flex flex-row flex-wrap gap-4 sm:hidden">
                          <PermBadges perms={u.start_permissions} testId={`user-perms-detail-${u.id}`} />
                          <RoleBadges roles={u.roles} testId={`user-roles-detail-${u.id}`} />
                        </div>
                      </dl>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">Hozzárendelt profilok ({linked.length})</p>
                          {linked.length === 0 ? <p className="text-slate-600">Nincs</p> : (
                            <ul className="mt-1 space-y-1" data-testid={`user-linked-${u.id}`}>
                              {linked.map((l) => <li key={l.id} className="text-slate-800">{l.name} <span className="font-mono text-xs text-slate-700">({l.identifier || "—"})</span>{l.birth_date ? <span className="text-slate-700"> · {l.birth_date}</span> : null}</li>)}
                            </ul>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">Hozzárendelve ide ({parents.length})</p>
                          {parents.length === 0 ? <p className="text-slate-600">Nincs</p> : (
                            <ul className="mt-1 space-y-1" data-testid={`user-parents-${u.id}`}>
                              {parents.map((pu) => <li key={pu.id} className="text-slate-800">{pu.name} <span className="font-mono text-xs text-slate-700">({pu.identifier || "—"})</span></li>)}
                            </ul>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap justify-end gap-2">
                        <button type="button" onClick={() => setEditUser(u)} className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-sky-700" data-testid={`admin-edit-user-${u.id}`} aria-label="Adatok szerkesztése">
                          <Settings size={15} /> Szerkesztés
                        </button>
                        {!u.is_admin && (
                          <button type="button" onClick={() => setLinksUser(u)} className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-sky-700" data-testid={`user-links-btn-${u.id}`}>
                            <Link2 size={15} /> Hozzárendelések szerkesztése
                          </button>
                        )}
                        {!u.is_admin && (
                          <button type="button" disabled={busy === u.id} onClick={() => toggleSuspend(u)} className="inline-flex items-center gap-1.5 rounded-xl bg-[#F97316] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#EA6C0A] disabled:opacity-60" data-testid={`admin-suspend-user-${u.id}`}>
                            {u.is_suspended ? <Unlock size={15} /> : <Ban size={15} />} {busy === u.id ? "Mentés..." : u.is_suspended ? "Felfüggesztés feloldása" : "Fiók felfüggesztése"}
                          </button>
                        )}
                        {u.is_child_account && (
                          <button type="button" onClick={() => setVerifyUser(u)} className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700" data-testid={`admin-verify-user-${u.id}`}>
                            <MailCheck size={15} /> Felhasználó hitelesítése
                          </button>
                        )}
                        <button type="button" disabled={u.is_admin || deletingId === u.id} onClick={() => removeUser(u)} className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50" data-testid={`admin-delete-user-${u.id}`}>
                          <Trash2 size={15} /> {u.is_admin ? "Admin nem törölhető" : deletingId === u.id ? "Törlés..." : "Fiók törlése"}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {modal && <AdminCreateModal type={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {linksUser && <LinksModal u={linksUser} onClose={() => setLinksUser(null)} onDone={() => { setLinksUser(null); load(); }} />}
      {editUser && <AdminEditUserModal user={editUser} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} />}
      {verifyUser && <VerifyModal user={verifyUser} onClose={() => setVerifyUser(null)} />}
    </div>
  );
}

function VerifyModal({ user, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await axios.post(`${API}/ugyfelkapu/admin/users/${user.id}/verify-request`, { email }, { params: { token: token() } }); toast.success(r.data.message); onClose(); }
    catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="verify-modal">
      <form onSubmit={submit} className="w-full max-w-md space-y-4 rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-slate-900">Felhasználó hitelesítése</h3><button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-600 hover:bg-slate-100" data-testid="verify-modal-close"><X size={22} /></button></div>
        <p className="text-sm text-slate-800"><span className="font-semibold">{user.name}</span> <span className="font-mono text-xs text-slate-700">({user.identifier})</span> gyerekfiókja normál fiókká alakul, miután a megadott e-mail-címre küldött hivatkozáson befejezi a regisztrációt (jelszó, adatok). Az azonosító, tagság, jogosultságok és foglalások megmaradnak.</p>
        <div><label className="mb-1 block text-sm font-medium text-slate-700">E-mail-cím *</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={loginInputClass} placeholder="nev@example.com" data-testid="verify-email-input" /></div>
        <button type="submit" disabled={busy} className="h-12 w-full rounded-xl bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="verify-send-btn">{busy ? "Küldés..." : "Beküldés"}</button>
      </form>
    </div>
  );
}

function AdminEditUserModal({ user, onClose, onDone }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", phone: user.phone || "", gender: user.gender || "", birth_date: user.birth_date || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.put(`${API}/ugyfelkapu/admin/users/${user.id}`, form, { params: { token: token() } });
      toast.success(res.data.message || "Mentve");
      onDone();
    } catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy(false);
  };
  const L = ({ t, children }) => <div><label className="mb-1 block text-sm font-medium text-slate-700">{t}</label>{children}</div>;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="admin-edit-user-modal">
      <form onSubmit={submit} className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Fiókadatok szerkesztése</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-600 hover:bg-slate-100" data-testid="admin-edit-close"><X size={22} /></button>
        </div>
        <p className="rounded-xl bg-slate-100 px-3 py-2 font-mono text-sm font-semibold tracking-wider text-slate-700" data-testid="admin-edit-identifier">Azonosító: {user.identifier || "—"} <span className="font-sans text-xs font-normal text-slate-700">(nem módosítható)</span></p>
        <L t="Név *"><input value={form.name} onChange={set("name")} className={loginInputClass} data-testid="admin-edit-name" /></L>
        <L t={user.is_child_account ? "E-mail-cím" : "E-mail-cím *"}><input type="email" value={form.email} onChange={set("email")} className={loginInputClass} data-testid="admin-edit-email" /></L>
        <L t="Telefonszám"><input value={form.phone} onChange={set("phone")} className={loginInputClass} data-testid="admin-edit-phone" /></L>
        <L t="Nem"><select value={form.gender} onChange={set("gender")} className={loginInputClass} data-testid="admin-edit-gender"><option value="">Nincs megadva</option><option value="ferfi">Férfi / Fiú</option><option value="no">Nő / Lány</option></select></L>
        <L t="Születési dátum"><input type="date" value={form.birth_date} onChange={set("birth_date")} className={loginInputClass} data-testid="admin-edit-birth-date" /></L>
        <p className="text-xs text-slate-700">Jogosultságok és rajtengedélyek: a Rajtengedély / Jogosultságok / Szerepkörök menüpontban.</p>
        <button type="submit" disabled={busy} className="h-12 w-full rounded-xl bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="admin-edit-save">{busy ? "Mentés..." : "Mentés"}</button>
      </form>
    </div>
  );
}

function AdminCreateModal({ type, onClose, onDone }) {
  const isChild = type === "child";
  const [form, setForm] = useState({ name: "", email: "", birth_date: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (isChild ? (!form.name || !form.birth_date) : (!form.name || !form.email || !form.birth_date)) {
      toast.error(isChild ? "Név és születési dátum kötelező!" : "Név, email és születési dátum kötelező!");
      return;
    }
    setBusy(true);
    try {
      const url = isChild ? `${API}/competition/users/child` : `${API}/competition/users/admin-create`;
      const body = isChild ? { name: form.name, birth_date: form.birth_date } : form;
      const res = await axios.post(url, body, { params: { token: token() } });
      toast.success(isChild ? `Gyerekfiók létrehozva! Azonosító: ${res.data.identifier}` : `Meghívó elküldve a(z) ${form.email} címre. Azonosító: ${res.data.identifier || res.data.user?.identifier || ""}`);
      onDone();
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="admin-create-modal">
      <form onSubmit={submit} className="w-full max-w-md space-y-4 rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{isChild ? "Gyerekfiók hozzáadása" : "Fiók hozzáadása"}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-600 hover:bg-slate-100 hover:text-slate-600" data-testid="admin-create-close"><X size={22} /></button>
        </div>
        <p className="text-sm text-slate-700">{isChild ? "A rendszer automatikusan generál azonosítót." : "A megadott e-mail-címre meghívó levelet küldünk; a felhasználó a benne lévő hivatkozáson fejezi be a regisztrációt (ugyanazon a fiókon és azonosítóval)."}</p>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Név *</label>
          <input value={form.name} onChange={set("name")} className={loginInputClass} placeholder={isChild ? "Gyerek neve" : "Teljes név"} data-testid="admin-create-name" />
        </div>
        {!isChild && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">E-mail-cím *</label>
            <input type="email" value={form.email} onChange={set("email")} className={loginInputClass} placeholder="pelda@email.hu" data-testid="admin-create-email" />
          </div>
        )}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Születési dátum *</label>
          <input type="date" value={form.birth_date} onChange={set("birth_date")} className={loginInputClass} data-testid="admin-create-birth-date" />
        </div>
        {!isChild && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Telefonszám</label>
            <input type="tel" value={form.phone} onChange={set("phone")} className={loginInputClass} placeholder="+36 30 123 4567" data-testid="admin-create-phone" />
          </div>
        )}
        <button type="submit" disabled={busy} className={`${primaryButtonClass} h-12 w-full`} data-testid="admin-create-submit">{busy ? "Küldés..." : isChild ? "Létrehozás" : "Meghívó küldése"}</button>
      </form>
    </div>
  );
}

export default FelhasznalokTab;

function LinksModal({ u, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [ids, setIds] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/ugyfelkapu/admin/users/${u.id}/links`, { params: { token: token() } }).then((r) => { setData(r.data); setIds(r.data.linked_ids); }).catch((e) => toast.error(getAuthErrorMessage(e))); }, [u.id]);
  const byId = (id) => data?.candidates.find((c) => c.id === id);
  const add = () => { if (!pick) return; if (pick === u.id) { toast.error("Saját magához nem rendelhető fiók"); return; } if (ids.includes(pick)) { toast.error("Ez a hozzárendelés már létezik"); return; } setIds([...ids, pick]); setPick(""); };
  const save = async () => { setBusy(true); try { const r = await axios.put(`${API}/ugyfelkapu/admin/users/${u.id}/links`, { linked_ids: ids }, { params: { token: token() } }); toast.success(r.data.message); onDone(); } catch (e) { toast.error(getAuthErrorMessage(e)); } setBusy(false); };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="links-modal">
      <div className="max-h-[92vh] w-full max-w-lg space-y-4 overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-slate-900">Hozzárendelések – {u.name}</h3><button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-600 hover:bg-slate-100" data-testid="links-modal-close"><X size={22} /></button></div>
        {!data ? <p className="text-slate-600">Betöltés...</p> : <>
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Jelenleg hozzárendelt profilok</p>
            {ids.length === 0 ? <p className="rounded-xl bg-slate-50 px-3 py-3 text-center text-sm text-slate-600" data-testid="links-empty">Nincs hozzárendelt profil.</p> : (
              <ul className="divide-y divide-slate-100 rounded-xl bg-slate-50 px-3" data-testid="links-list">{ids.map((id) => { const c = byId(id); return (
                <li key={id} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid={`link-row-${id}`}>
                  <span className="text-slate-900"><span className="font-semibold">{c?.name || "Ismeretlen fiók"}</span> <span className="font-mono text-xs text-slate-700">{c?.identifier || ""}</span>{c?.is_child_account ? <span className="ml-1 text-xs text-slate-600">· gyerekfiók</span> : null}</span>
                  <button type="button" onClick={() => setIds(ids.filter((x) => x !== id))} className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-700" data-testid={`link-remove-${id}`}><Trash2 size={13} /> Eltávolítás</button>
                </li>
              ); })}</ul>
            )}
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Fiók hozzárendelése</p>
            <div className="flex gap-2">
              <select value={pick} onChange={(e) => setPick(e.target.value)} className={loginInputClass} data-testid="links-picker">
                <option value="">Válassz fiókot…</option>
                {data.candidates.filter((c) => !ids.includes(c.id)).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.identifier || "—"}){c.is_child_account ? " – gyerekfiók" : ""}</option>)}
              </select>
              <button type="button" onClick={add} disabled={!pick} className="shrink-0 rounded-xl bg-slate-800 px-4 font-semibold text-white hover:bg-slate-900 disabled:opacity-50" data-testid="links-add-btn">Hozzáadás</button>
            </div>
          </div>
          <p className="text-xs text-slate-700">A mentett hozzárendelés külön kérelem nélkül azonnal jóváhagyott; törléskor csak a kapcsolat szűnik meg, a fiókok megmaradnak.</p>
          <button type="button" onClick={save} disabled={busy} className="h-12 w-full rounded-xl bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="links-save-btn">{busy ? "Mentés..." : "Mentés"}</button>
        </>}
      </div>
    </div>
  );
}
